import { isValidContactPhone } from '@/lib/contactFields';

/**
 * MedCom XREF01 Patient fields stored on the stamkort.
 * personTitle is an..35. TelephoneSubscriber is at most three, each with
 * PatTlf (an..25) and a TLFTYPE code. The codes are the qualifier values
 * used in MedCom's REF01 examples: fastnet, mobil, arbejde.
 * The first entry is the primary operational number. patients.phone is a
 * read-only mirror of that identifier (kept in sync by a database trigger).
 */

export const MEDCOM_JOB_TITLE_MAX = 35;
export const MEDCOM_TELEPHONE_IDENTIFIER_MAX = 25;
export const MEDCOM_MAX_TELEPHONE_SUBSCRIBERS = 3;

export const MEDCOM_TELEPHONE_CODES = ['fastnet', 'mobil', 'arbejde'] as const;
export type MedcomTelephoneCode = (typeof MEDCOM_TELEPHONE_CODES)[number];
export const DEFAULT_TELEPHONE_CODE: MedcomTelephoneCode = 'mobil';

export type MedcomTelephoneSubscriber = {
  identifier: string;
  code: MedcomTelephoneCode;
};

export type TelephoneSubscriberDraft = {
  identifier: string;
  code: MedcomTelephoneCode | '';
};

export function emptyTelephoneDraft(): TelephoneSubscriberDraft {
  return { identifier: '', code: DEFAULT_TELEPHONE_CODE };
}

export type StamkortFieldMessages = {
  jobTitleTooLong: string;
  invalidPhone: string;
  typeRequired: string;
  identifierTooLong: string;
  tooMany: string;
};

export type SubscriberFieldError = {
  identifier?: string;
  code?: string;
};

export type StamkortFieldErrorTexts = {
  canSave: boolean;
  jobTitle?: string;
  tooMany?: string;
  subscribers: SubscriberFieldError[];
};

export function isMedcomTelephoneCode(value: string): value is MedcomTelephoneCode {
  return (MEDCOM_TELEPHONE_CODES as readonly string[]).includes(value);
}

export function readTelephoneSubscribers(value: unknown): MedcomTelephoneSubscriber[] {
  if (!Array.isArray(value)) return [];
  const subscribers: MedcomTelephoneSubscriber[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const record = item as Record<string, unknown>;
    const identifier = typeof record.identifier === 'string' ? record.identifier.trim() : '';
    const code = typeof record.code === 'string' ? record.code.trim() : '';
    if (identifier.length === 0 || !isMedcomTelephoneCode(code)) continue;
    subscribers.push({ identifier, code });
  }
  return subscribers;
}

export function telephoneSubscriberDrafts(value: unknown): TelephoneSubscriberDraft[] {
  return readTelephoneSubscribers(value).map((subscriber) => ({
    identifier: subscriber.identifier,
    code: subscriber.code,
  }));
}

/** Digits-only compare key. Strips a leading 00 and Danish 45 when 8 digits remain. */
export function telephoneMatchKey(value: string | null | undefined): string {
  let digits = (value ?? '').replace(/[^0-9]/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.length === 10 && digits.startsWith('45')) digits = digits.slice(2);
  return digits;
}

/** First Telefonnumre identifier, else the legacy phone mirror. */
export function primaryTelephoneNumber(
  subscribers: unknown,
  fallback?: string | null,
): string | null {
  const first = readTelephoneSubscribers(subscribers)[0]?.identifier.trim() ?? '';
  if (first.length > 0) return first;
  const extra = fallback?.trim() ?? '';
  return extra.length > 0 ? extra : null;
}

export function patientTelephoneNumbers(
  subscribers: unknown,
  fallback?: string | null,
): MedcomTelephoneSubscriber[] {
  const list = readTelephoneSubscribers(subscribers);
  if (list.length > 0) return list;
  const extra = fallback?.trim() ?? '';
  if (extra.length === 0) return [];
  return [{ identifier: extra, code: 'mobil' }];
}

/** All Telefonnumre identifiers for display. Empty string when none. */
export function formatPatientTelephoneNumbers(
  subscribers: unknown,
  fallback?: string | null,
  options?: {
    withTypes?: boolean;
    typeLabel?: (code: MedcomTelephoneCode) => string;
    separator?: string;
  },
): string {
  const list = patientTelephoneNumbers(subscribers, fallback);
  if (list.length === 0) return '';
  const separator = options?.separator ?? ' · ';
  if (options?.withTypes && options.typeLabel) {
    return list
      .map((subscriber) => `${subscriber.identifier} (${options.typeLabel!(subscriber.code)})`)
      .join(separator);
  }
  return list.map((subscriber) => subscriber.identifier).join(separator);
}

function rowIsBlank(row: TelephoneSubscriberDraft): boolean {
  return row.identifier.trim().length === 0;
}

/** New rows and numbers typed without a type become Mobil. */
export function applyDefaultTelephoneTypes(
  subscribers: readonly TelephoneSubscriberDraft[],
): TelephoneSubscriberDraft[] {
  return subscribers.map((row) => {
    if (row.identifier.trim().length > 0 && row.code === '') {
      return { ...row, code: DEFAULT_TELEPHONE_CODE };
    }
    return row;
  });
}

export function firstStamkortFocusId(errors: StamkortFieldErrorTexts): string | null {
  if (errors.jobTitle) return 'job_title';
  for (let index = 0; index < errors.subscribers.length; index += 1) {
    const row = errors.subscribers[index];
    if (row?.identifier) return `telephone_subscriber_${index}`;
    if (row?.code) return `patient-telephone-type-${index}`;
  }
  return null;
}

export function focusPatientField(id: string | null): void {
  if (!id) return;
  const element = document.getElementById(id);
  if (!(element instanceof HTMLElement)) return;
  element.focus();
  element.scrollIntoView({ block: 'center', behavior: 'smooth' });
}

export function stamkortFieldErrorTexts(
  submitted: boolean,
  jobTitle: string,
  subscribers: readonly TelephoneSubscriberDraft[],
  messages: StamkortFieldMessages,
): StamkortFieldErrorTexts {
  const jobTitleTooLong = jobTitle.trim().length > MEDCOM_JOB_TITLE_MAX;
  const tooMany = subscribers.length > MEDCOM_MAX_TELEPHONE_SUBSCRIBERS;
  const subscriberErrors: SubscriberFieldError[] = subscribers.map((row) => {
    if (rowIsBlank(row)) return {};
    const error: SubscriberFieldError = {};
    const identifier = row.identifier.trim();
    if (identifier.length === 0 || !isValidContactPhone(identifier)) {
      error.identifier = messages.invalidPhone;
    } else if (identifier.length > MEDCOM_TELEPHONE_IDENTIFIER_MAX) {
      error.identifier = messages.identifierTooLong;
    }
    if (row.code === '') {
      error.code = messages.typeRequired;
    }
    return error;
  });

  const subscribersValid = subscriberErrors.every(
    (error) => error.identifier === undefined && error.code === undefined,
  );
  const canSave = !jobTitleTooLong && !tooMany && subscribersValid;

  if (!submitted || canSave) {
    return { canSave, subscribers: subscribers.map(() => ({})) };
  }

  return {
    canSave,
    jobTitle: jobTitleTooLong ? messages.jobTitleTooLong : undefined,
    tooMany: tooMany ? messages.tooMany : undefined,
    subscribers: subscriberErrors,
  };
}

export function stamkortPayload(
  jobTitle: string,
  subscribers: readonly TelephoneSubscriberDraft[],
): {
  job_title: string | null;
  telephone_subscribers: MedcomTelephoneSubscriber[] | null;
} {
  const title = jobTitle.trim();
  const telephoneSubscribers = applyDefaultTelephoneTypes(subscribers)
    .filter((row) => !rowIsBlank(row))
    .map((row) => ({
      identifier: row.identifier.trim(),
      code: row.code === '' ? DEFAULT_TELEPHONE_CODE : row.code,
    }));

  return {
    job_title: title.length > 0 ? title : null,
    telephone_subscribers: telephoneSubscribers.length > 0 ? telephoneSubscribers : null,
  };
}
